import React, { useState, useRef, useEffect } from 'react';
import { useRestaurants } from '@/hooks/useRestaurants';
import { useDocumentTemplate } from '@/hooks/useDocumentTemplates';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Building2, Loader2, ChevronRight, Download, Printer, Shield } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export const NDADocumentV3: React.FC = () => {
  const { data: clients, isLoading: loadingClients } = useRestaurants();
  const { data: template, isLoading: loadingTemplate } = useDocumentTemplate('nda');
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [showDocument, setShowDocument] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const documentRef = useRef<HTMLDivElement>(null);

  const [clientInfo, setClientInfo] = useState({
    companyName: '',
    address: '',
    representedBy: '',
    title: '',
    rcs: '',
    siret: '',
  });

  const handleClientSelect = (clientId: string) => {
    setSelectedClientId(clientId);
    const client = clients?.find(c => c.id === clientId);
    
    if (client) {
      const primaryContact = client.primary_contact as any;
      setClientInfo({
        companyName: client.name,
        address: client.location || '',
        representedBy: primaryContact?.name || '',
        title: primaryContact?.role || '',
        rcs: client.rcs || '',
        siret: client.siret || '',
      });
      setShowDocument(true);
    }
  };

  const handleDownloadPDF = async () => {
    if (!documentRef.current || isGenerating) return;
    
    setIsGenerating(true);
    
    try {
      const element = documentRef.current;
      
      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: 1200,
      });

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: 'a4',
        hotfixes: ['px_scaling']
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 0;
      const totalPages = Math.ceil(imgHeight / pdfHeight);

      while (heightLeft > 0) {
        if (pageNumber > 0) {
          pdf.addPage();
        }
        
        pdf.addImage(
          canvas.toDataURL('image/png'),
          'PNG',
          0,
          position,
          imgWidth,
          imgHeight,
          undefined,
          'FAST'
        );
        
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(
          `Page ${pageNumber + 1} / ${totalPages}`,
          pdfWidth / 2,
          pdfHeight - 20,
          { align: 'center' }
        );
        
        heightLeft -= pdfHeight;
        position -= pdfHeight;
        pageNumber++;
      }

      const fileName = clientInfo.companyName 
        ? `NDA_STRATOS_${clientInfo.companyName.replace(/\s+/g, '_')}.pdf`
        : 'NDA_STRATOS_Hospitality.pdf';
      
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
    } finally {
      setIsGenerating(false);
    }
  };

  if (loadingClients || loadingTemplate) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-stratos-gold animate-spin" />
      </div>
    );
  }

  if (!template) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-gray-600 mb-4">Template NDA non disponible</p>
          <p className="text-sm text-gray-500">
            Exécutez la migration 005_create_document_templates.sql dans Supabase
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!showDocument) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Générer un NDA</CardTitle>
          <CardDescription>
            Sélectionnez un client pour pré-remplir les informations
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="client">Client / Prospect</Label>
            <select
              id="client"
              value={selectedClientId || ''}
              onChange={(e) => e.target.value && handleClientSelect(e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2 text-sm mt-1"
            >
              <option value="">Sélectionnez un client</option>
              {clients?.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name} - {client.location || 'Non renseigné'} ({client.status})
                </option>
              ))}
            </select>
          </div>

          {clients && clients.length === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800 text-sm">
                Aucun client disponible. Ajoutez d'abord des clients dans la page d'accueil.
              </p>
            </div>
          )}

          <div className="pt-4 border-t">
            <Button
              onClick={() => setShowDocument(true)}
              variant="outline"
              className="w-full"
            >
              Créer un NDA sans client pré-sélectionné
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const templateContent = template.content;

  return (
    <div className="space-y-6">
      {/* Form Section */}
      <Card className="p-6 print:hidden">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {templateContent.title}
            </h2>
            {selectedClientId && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                <Building2 className="w-4 h-4" />
                <span>
                  Client sélectionné :{' '}
                  <strong>{clients?.find(c => c.id === selectedClientId)?.name}</strong>
                </span>
              </div>
            )}
          </div>
          <Button
            onClick={() => {
              setShowDocument(false);
              setSelectedClientId(null);
              setClientInfo({
                companyName: '',
                address: '',
                representedBy: '',
                title: '',
                rcs: '',
                siret: '',
              });
            }}
            variant="outline"
            size="sm"
          >
            ← Changer de client
          </Button>
        </div>
        
        <div className="space-y-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="companyName">Nom du Client / Société</Label>
              <Input
                id="companyName"
                value={clientInfo.companyName}
                onChange={(e) => setClientInfo(prev => ({ ...prev, companyName: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="address">Adresse complète du client</Label>
              <Input
                id="address"
                value={clientInfo.address}
                onChange={(e) => setClientInfo(prev => ({ ...prev, address: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="representedBy">Représenté par (nom, prénom)</Label>
              <Input
                id="representedBy"
                value={clientInfo.representedBy}
                onChange={(e) => setClientInfo(prev => ({ ...prev, representedBy: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="title">Fonction</Label>
              <Input
                id="title"
                value={clientInfo.title}
                onChange={(e) => setClientInfo(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="rcs">Numéro RCS</Label>
              <Input
                id="rcs"
                value={clientInfo.rcs}
                onChange={(e) => setClientInfo(prev => ({ ...prev, rcs: e.target.value }))}
                placeholder="Paris B 123 456 789"
              />
            </div>
            <div>
              <Label htmlFor="siret">Numéro SIRET</Label>
              <Input
                id="siret"
                value={clientInfo.siret}
                onChange={(e) => setClientInfo(prev => ({ ...prev, siret: e.target.value }))}
                placeholder="12345678900012"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <Button onClick={() => window.print()}>
            <Printer className="w-4 h-4 mr-2" />
            Imprimer
          </Button>
          <Button variant="outline" onClick={handleDownloadPDF} disabled={isGenerating}>
            <Download className="w-4 h-4 mr-2" />
            {isGenerating ? 'Génération...' : 'Télécharger PDF'}
          </Button>
        </div>
        {isGenerating && (
          <p className="text-sm text-blue-600 mt-3 animate-pulse">
            ⏳ Génération du PDF en cours... Veuillez patienter.
          </p>
        )}
      </Card>

      {/* Document */}
      <div ref={documentRef}>
        <Card className="p-12 bg-white print:shadow-none print:border-0 max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 pb-8 border-b-2 border-stratos-gold">
            <h1 
              className="text-5xl font-bold mb-2"
              style={{
                background: 'linear-gradient(to right, #E9BC66, #F4D89E)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              STRATOS HOSPITALITY
            </h1>
            <p className="text-sm text-gray-500 mb-8">by Getup Agency</p>
            
            <h2 className="text-3xl font-bold text-gray-900 mt-8 mb-4">
              {templateContent.title}
            </h2>
            {templateContent.subtitle && (
              <p className="text-xl text-gray-600 mb-8">{templateContent.subtitle}</p>
            )}
            
            <Badge variant="default" className="px-4 py-2">
              <Shield className="w-4 h-4 mr-2 inline" />
              Document Juridique Confidentiel
            </Badge>
          </div>

          {/* Préambule */}
          {templateContent.preambule && (
            <div className="mb-8 p-6 bg-blue-50 border-l-4 border-blue-600">
              <h3 className="text-lg font-bold text-gray-900 mb-3">
                PRÉAMBULE
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                {templateContent.preambule}
              </p>
            </div>
          )}

          {/* Articles */}
          <div className="space-y-8 text-sm text-gray-700 leading-relaxed">
            {templateContent.articles.map((article, index) => {
              // Remplacer les placeholders dans l'Article 1
              let content = article.content;
              if (article.number === 1) {
                let clientDetails = clientInfo.companyName || '[Nom du Client / Société]';
                
                // Ajouter RCS/SIRET si disponibles
                if (clientInfo.rcs || clientInfo.siret) {
                  const legalInfo = [];
                  if (clientInfo.rcs) legalInfo.push(`RCS : ${clientInfo.rcs}`);
                  if (clientInfo.siret) legalInfo.push(`SIRET : ${clientInfo.siret}`);
                  clientDetails += ` (${legalInfo.join(', ')})`;
                }
                
                content = content
                  .replace('[Nom du Client / Société]', clientDetails)
                  .replace('[adresse complète]', clientInfo.address || '[adresse complète]')
                  .replace('[Nom, prénom, fonction]', `${clientInfo.representedBy || '[Nom, prénom]'}, ${clientInfo.title || '[fonction]'}`);
              }

              return (
                <section key={article.number} className="print:break-inside-avoid">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    ARTICLE {article.number} – {article.title}
                  </h3>
                  <div className="whitespace-pre-line">
                    {content.split('\n').map((paragraph, i) => (
                      <p key={i} className={paragraph.startsWith('•') ? 'ml-4 mb-2' : 'mb-3'}>
                        {paragraph}
                      </p>
                    ))}
                  </div>

                  {/* Spacer après article 11 pour forcer nouvelle page */}
                  {article.number === 11 && (
                    <div className="h-96 print:h-screen"></div>
                  )}

                  {/* Signatures pour article 12 */}
                  {article.number === 12 && (
                    <>
                      <div className="text-center text-sm text-gray-700 my-8">
                        <p>{article.content}</p>
                      </div>

                      <div className="grid grid-cols-2 gap-8 pt-6 border-t-2 border-stratos-gold">
                        <div className="space-y-4">
                          <p className="font-bold text-gray-900 mb-4">STRATOS Hospitality</p>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Représentée par :</p>
                            <p className="font-semibold text-gray-900">Adrien Cerdan, Gérant</p>
                          </div>
                          <div className="pt-8">
                            <p className="text-xs text-gray-600 mb-2">Signature :</p>
                            <div className="border-b border-gray-300 h-20"></div>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <p className="font-bold text-gray-900 mb-4">
                            {clientInfo.companyName || '[Nom du Client / Société]'}
                          </p>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Représentée par :</p>
                            <p className="font-semibold text-gray-900">
                              {clientInfo.representedBy || '__________________________'}
                            </p>
                            <p className="text-xs text-gray-600">
                              {clientInfo.title || '__________________________'}
                            </p>
                          </div>
                          <div className="pt-8">
                            <p className="text-xs text-gray-600 mb-2">Signature :</p>
                            <div className="border-b border-gray-300 h-20"></div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </section>
              );
            })}
          </div>

          {/* Footer */}
          {templateContent.footer && (
            <div className="mt-12 pt-6 border-t text-center text-xs text-gray-500">
              <p className="whitespace-pre-line">{templateContent.footer}</p>
              <p className="mt-2">© STRATOS Hospitality 2025 – Tous droits réservés</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

